import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { RefreshCw } from 'lucide-react'
import { getAdminApiLogDetail, type AdminApiLogDetail } from '@/src/lib/api/admin-analytics'
import { format } from 'date-fns'

interface AdminApiLogDetailModalProps {
  logId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function JsonBlock({ data }: { data: Record<string, any> | null | undefined }) {
  if (data === null || data === undefined) {
    return <p className="text-sm text-muted-foreground italic">ไม่มีข้อมูล</p>
  }
  return (
    <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-80 whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-muted-foreground w-36 shrink-0">{label}</span>
      <span className="font-medium text-foreground break-all">{value ?? <span className="text-muted-foreground italic">-</span>}</span>
    </div>
  )
}

function getStatusBadge(status: number | null) {
  if (!status) return <Badge variant="secondary">-</Badge>
  if (status >= 200 && status < 300) return <Badge className="bg-green-500 text-white">{status}</Badge>
  if (status >= 400 && status < 500) return <Badge className="bg-yellow-500 text-white">{status}</Badge>
  if (status >= 500) return <Badge className="bg-red-500 text-white">{status}</Badge>
  return <Badge variant="secondary">{status}</Badge>
}

export function AdminApiLogDetailModal({ logId, open, onOpenChange }: AdminApiLogDetailModalProps) {
  const [log, setLog] = useState<AdminApiLogDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !logId) return

    const fetchDetail = async () => {
      try {
        setLoading(true)
        setError(null)
        setLog(null)
        const response = await getAdminApiLogDetail(logId)
        setLog(response.data)
      } catch (err: any) {
        setError(err.message || 'ไม่สามารถโหลดรายละเอียดได้')
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [logId, open])

  const hasBankResponse =
    log?.bankResponse &&
    typeof log.bankResponse === 'object' &&
    Object.keys(log.bankResponse).length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">รายละเอียด API Log</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span className="text-sm">กำลังโหลด...</span>
          </div>
        )}

        {error && (
          <div className="py-8 text-center text-sm text-red-500">{error}</div>
        )}

        {log && !loading && (
          <div className="space-y-4">
            {/* Summary bar */}
            <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <span className="font-semibold text-blue-600 text-sm">{log.method}</span>
              <span className="font-mono text-sm text-foreground flex-1 min-w-0 truncate">{log.endpoint}</span>
              {getStatusBadge(log.responseStatus)}
              {log.processingTimeMs !== null && (
                <span className="text-xs text-muted-foreground">{log.processingTimeMs}ms</span>
              )}
            </div>

            {/* Meta info */}
            <div className="space-y-1.5 px-1">
              {log.customerName && (
                <InfoRow label="ลูกค้า" value={
                  <div>
                    <div className="font-medium">{log.customerName}</div>
                    {log.customerEmail && <div className="text-xs text-muted-foreground">{log.customerEmail}</div>}
                  </div>
                } />
              )}
              <InfoRow label="Request ID" value={<span className="font-mono text-xs">{log.requestId}</span>} />
              {log.correlationId && (
                <InfoRow label="Correlation ID" value={<span className="font-mono text-xs">{log.correlationId}</span>} />
              )}
              {log.verificationId && (
                <InfoRow label="Verification ID" value={<span className="font-mono text-xs">{log.verificationId}</span>} />
              )}
              {log.generationId && (
                <InfoRow label="Generation ID" value={<span className="font-mono text-xs">{log.generationId}</span>} />
              )}
              <InfoRow label="IP Address" value={log.ipAddress} />
              <InfoRow label="เวลา" value={log.createdAt ? format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss') : null} />
              {log.errorCode && (
                <InfoRow
                  label={log.responseStatus !== null && log.responseStatus < 400 ? 'App Code' : 'Error Code'}
                  value={
                    <span className={log.responseStatus !== null && log.responseStatus < 400
                      ? 'text-muted-foreground font-mono text-xs'
                      : 'text-red-500 font-mono text-xs'
                    }>{log.errorCode}</span>
                  }
                />
              )}
              {log.errorMessage && (
                <InfoRow
                  label={log.responseStatus !== null && log.responseStatus < 400 ? 'Message' : 'Error Message'}
                  value={
                    <span className={log.responseStatus !== null && log.responseStatus < 400
                      ? 'text-muted-foreground text-xs'
                      : 'text-red-500 text-xs'
                    }>{log.errorMessage}</span>
                  }
                />
              )}
            </div>

            {/* Tabs: Request / Response / Bank Response */}
            <Tabs defaultValue="request">
              <TabsList className={`grid w-full ${hasBankResponse ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="request">Request</TabsTrigger>
                <TabsTrigger value="response">Response</TabsTrigger>
                {hasBankResponse && (
                  <TabsTrigger value="bank">Bank Response</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="request" className="space-y-3 mt-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">Headers</p>
                  <JsonBlock data={log.requestHeaders} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">Body</p>
                  <JsonBlock data={log.requestBody} />
                </div>
              </TabsContent>

              <TabsContent value="response" className="space-y-3 mt-3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                  {getStatusBadge(log.responseStatus)}
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">Body</p>
                  <JsonBlock data={log.responseBody} />
                </div>
              </TabsContent>

              {hasBankResponse && (
                <TabsContent value="bank" className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">Bank Verification Response</p>
                  <JsonBlock data={log.bankResponse} />
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
